/**
 * Admin-Seite: Shop-Katalog verwalten.
 *
 * Admin & Backoffice können:
 *   • Alle Produkte (inkl. ausverkauft) tabellarisch sehen
 *   • Neues Produkt anlegen (Modal mit allen Pflichtfeldern)
 *   • Produkt editieren (Sheet rechts)
 *   • Produkt löschen (mit Confirm)
 *   • Produktbild hochladen / entfernen
 *   • SKU, Preis, Kategorie, Größen, Farben, Verfügbarkeit ändern
 */
import React, { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter, SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Image as ImageIcon, Upload, X, Loader2,
  ShoppingBag, Package, CheckCircle2, EyeOff, Search,
} from "lucide-react";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { v: "bekleidung",  l: "Bekleidung" },
  { v: "werbemittel", l: "Werbemittel" },
  { v: "giveaways",   l: "Give-Aways" },
  { v: "buero",       l: "Büro" },
];

const COLOR_OPTIONS = ["emerald", "cyan", "rose", "amber", "violet"];

const fmtEuro = (cents) => `${((cents || 0) / 100).toFixed(2).replace(".", ",")} €`;

function imageUrl(path) {
  if (!path) return null;
  const token = typeof localStorage !== "undefined" ? (localStorage.getItem("access_token") || "") : "";
  return `${BACKEND_URL}/api/files/download/${path}?auth=${token}`;
}

const EMPTY_PRODUCT = {
  sku: "",
  name: "",
  category: "bekleidung",
  description: "",
  price_cents: 0,
  icon: "Package",
  color: "emerald",
  sizes: [],
  colors: [],
  min_qty: 1,
  qty_step: 1,
  unit_label: "Stück",
  in_stock: true,
  sort_order: 100,
};

export default function AdminShop() {
  return (
    <Layout title="Shop-Verwaltung" subtitle="Produkte anlegen, editieren und Bilder hochladen — der User sieht den Katalog sofort.">
      <AdminShopPanel />
    </Layout>
  );
}

export function AdminShopPanel() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} | product
  const [deletingId, setDeletingId] = useState(null);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shop/admin/products");
      setProducts(data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    const s = search.trim().toLowerCase();
    return products.filter((p) => {
      if (filterCat !== "all" && p.category !== filterCat) return false;
      if (!s) return true;
      return (
        (p.name || "").toLowerCase().includes(s) ||
        (p.sku || "").toLowerCase().includes(s) ||
        (p.description || "").toLowerCase().includes(s)
      );
    });
  }, [products, search, filterCat]);

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await api.delete(`/shop/admin/products/${deletingId}`);
      toast.success("Produkt gelöscht");
      setDeletingId(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <>
      <div className="space-y-5" data-testid="admin-shop-page">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              placeholder="Suchen (Name, SKU, Beschreibung)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="admin-shop-search"
            />
          </div>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-[180px]" data-testid="admin-shop-cat-filter">
              <SelectValue placeholder="Alle Kategorien" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Kategorien</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            onClick={() => setEditing({ ...EMPTY_PRODUCT })}
            className="brand-gradient-bg text-white"
            data-testid="admin-shop-new"
          >
            <Plus className="h-4 w-4 mr-2" /> Neues Produkt
          </Button>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-4 gap-3">
          <KPI label="Produkte gesamt" value={products.length} icon={Package} />
          <KPI label="Verfügbar" value={products.filter((p) => p.in_stock).length} icon={CheckCircle2} tone="emerald" />
          <KPI label="Ausverkauft" value={products.filter((p) => !p.in_stock).length} icon={EyeOff} tone="zinc" />
          <KPI label="Mit Foto" value={products.filter((p) => p.image_path).length} icon={ImageIcon} tone="cyan" />
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
          </div>
        ) : visible.length === 0 ? (
          <Card className="p-12 text-center" data-testid="admin-shop-empty">
            <ShoppingBag className="h-10 w-10 mx-auto mb-3 text-zinc-300" />
            <div className="font-display font-bold">Keine Produkte gefunden</div>
            <p className="text-sm text-zinc-500 mt-1">Lege dein erstes Produkt an oder ändere die Filter.</p>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-shop-table">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-bold text-zinc-700">Bild</th>
                    <th className="px-4 py-3 font-bold text-zinc-700">SKU / Name</th>
                    <th className="px-4 py-3 font-bold text-zinc-700">Kategorie</th>
                    <th className="px-4 py-3 font-bold text-zinc-700 text-right">Preis (netto)</th>
                    <th className="px-4 py-3 font-bold text-zinc-700">Einheit</th>
                    <th className="px-4 py-3 font-bold text-zinc-700">Status</th>
                    <th className="px-4 py-3 font-bold text-zinc-700 text-right">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {visible.map((p) => (
                    <tr key={p.id} className="hover:bg-zinc-50/60" data-testid={`admin-shop-row-${p.sku}`}>
                      <td className="px-4 py-2">
                        <div className="h-12 w-12 rounded-lg overflow-hidden bg-zinc-100 flex items-center justify-center">
                          {p.image_path ? (
                            <img src={imageUrl(p.image_path)} alt={p.name} className="w-full h-full object-cover" />
                          ) : (
                            <ImageIcon className="h-5 w-5 text-zinc-300" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="font-bold text-zinc-900">{p.name}</div>
                        <div className="text-[11px] text-zinc-400 font-mono">{p.sku}</div>
                      </td>
                      <td className="px-4 py-2">
                        <Badge variant="outline" className="text-[11px]">
                          {CATEGORIES.find((c) => c.v === p.category)?.l || p.category}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums">{fmtEuro(p.price_cents)}</td>
                      <td className="px-4 py-2 text-zinc-600">{p.unit_label}</td>
                      <td className="px-4 py-2">
                        {p.in_stock ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">Verfügbar</Badge>
                        ) : (
                          <Badge className="bg-zinc-100 text-zinc-500 border border-zinc-200">Ausverkauft</Badge>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditing(p)}
                          data-testid={`admin-shop-edit-${p.sku}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeletingId(p.id)}
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          data-testid={`admin-shop-del-${p.sku}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Edit / Create sheet */}
      {editing && (
        <ProductSheet
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {/* Delete confirm */}
      <AlertDialog open={!!deletingId} onOpenChange={(o) => !o && setDeletingId(null)}>
        <AlertDialogContent data-testid="admin-shop-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Produkt wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Das Produkt verschwindet sofort aus dem User-Katalog.
              Bereits aufgegebene Bestellungen bleiben unverändert (gespeichert mit ihren Werten zum Bestellzeitpunkt).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-shop-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="admin-shop-delete-confirm">
              Endgültig löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function KPI({ label, value, icon: Icon, tone = "emerald" }) {
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    cyan:    "bg-cyan-50 text-cyan-700 border-cyan-200",
    zinc:    "bg-zinc-100 text-zinc-600 border-zinc-200",
  };
  return (
    <Card className="p-4 flex items-center gap-3">
      <div className={cn("h-10 w-10 rounded-lg border flex items-center justify-center", toneMap[tone])}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-bold">{label}</div>
        <div className="text-2xl font-display font-bold tabular-nums">{value}</div>
      </div>
    </Card>
  );
}

function ProductSheet({ initial, onClose, onSaved }) {
  const isCreate = !initial.id;
  const [form, setForm] = useState(() => ({ ...initial, sizes: initial.sizes || [], colors: initial.colors || [] }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.sku.trim() || !form.name.trim()) {
      toast.error("SKU und Name sind Pflichtfelder");
      return;
    }
    setSaving(true);
    try {
      let saved;
      if (isCreate) {
        const payload = {
          sku: form.sku.trim(),
          name: form.name.trim(),
          category: form.category,
          description: form.description || "",
          price_cents: Number(form.price_cents) || 0,
          icon: form.icon || "Package",
          color: form.color || "emerald",
          sizes: form.sizes || [],
          colors: form.colors || [],
          min_qty: Number(form.min_qty) || 1,
          qty_step: Number(form.qty_step) || 1,
          unit_label: form.unit_label || "Stück",
          in_stock: !!form.in_stock,
          sort_order: Number(form.sort_order) || 0,
        };
        const { data } = await api.post("/shop/admin/products", payload);
        saved = data.product;
        toast.success("Produkt angelegt");
      } else {
        const payload = {
          sku: form.sku.trim(),
          name: form.name.trim(),
          category: form.category,
          description: form.description || "",
          price_cents: Number(form.price_cents) || 0,
          icon: form.icon || "Package",
          color: form.color || "emerald",
          sizes: form.sizes || [],
          colors: form.colors || [],
          min_qty: Number(form.min_qty) || 1,
          qty_step: Number(form.qty_step) || 1,
          unit_label: form.unit_label || "Stück",
          in_stock: !!form.in_stock,
          sort_order: Number(form.sort_order) || 0,
        };
        const { data } = await api.patch(`/shop/admin/products/${initial.id}`, payload);
        saved = data.product;
        toast.success("Produkt aktualisiert");
      }
      // Reflect updated values in local state (in case image was uploaded between save attempts)
      setForm((f) => ({ ...f, ...saved }));
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onPickImage = async (file) => {
    if (!file) return;
    if (!initial.id) {
      toast.error("Bitte zuerst Produkt speichern, danach Bild hochladen.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/shop/admin/products/${initial.id}/image`, fd);
      set("image_path", data.image_path);
      toast.success("Bild hochgeladen");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const removeImage = async () => {
    if (!initial.id || !form.image_path) return;
    try {
      await api.delete(`/shop/admin/products/${initial.id}/image`);
      set("image_path", null);
      toast.success("Bild entfernt");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="admin-shop-sheet">
        <SheetHeader>
          <SheetTitle>{isCreate ? "Neues Produkt anlegen" : `Produkt bearbeiten: ${form.name}`}</SheetTitle>
          <SheetDescription>
            {isCreate
              ? "Fülle die Pflichtfelder aus und speichere — danach kannst du ein Foto hochladen."
              : "Änderungen werden sofort für alle User im Shop sichtbar."}
          </SheetDescription>
        </SheetHeader>

        <div className="py-5 space-y-5">
          {/* Image upload */}
          {!isCreate && (
            <Field label="Produktfoto">
              <div className="flex items-start gap-4">
                <div className="h-32 w-32 rounded-xl overflow-hidden bg-zinc-100 border-2 border-dashed border-zinc-200 flex items-center justify-center shrink-0">
                  {form.image_path ? (
                    <img src={imageUrl(form.image_path)} alt={form.name} className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-zinc-300" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <label className="block">
                    <Button asChild variant="outline" className="cursor-pointer" disabled={uploading}>
                      <span>
                        {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                        {form.image_path ? "Bild ersetzen" : "Bild hochladen"}
                      </span>
                    </Button>
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={(e) => onPickImage(e.target.files?.[0])}
                      className="hidden"
                      data-testid="admin-shop-image-input"
                    />
                  </label>
                  {form.image_path && (
                    <Button variant="ghost" size="sm" onClick={removeImage} className="text-rose-600">
                      <X className="h-4 w-4 mr-2" /> Bild entfernen
                    </Button>
                  )}
                  <p className="text-[11px] text-zinc-400">JPG / PNG / WEBP · max. 10 MB · wird auf 1400 px komprimiert.</p>
                </div>
              </div>
            </Field>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="SKU" required>
              <Input
                value={form.sku}
                onChange={(e) => set("sku", e.target.value.toUpperCase())}
                placeholder="z. B. BK-POLO-EG"
                className="font-mono"
                data-testid="admin-shop-field-sku"
              />
            </Field>
            <Field label="Kategorie" required>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="admin-shop-field-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Produktname" required>
            <Input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="z. B. Polo-Shirt Easy Gutachter"
              data-testid="admin-shop-field-name"
            />
          </Field>

          <Field label="Beschreibung">
            <Textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={3}
              placeholder="Kurze Produktbeschreibung — sichtbar im Shop unter dem Namen."
              data-testid="admin-shop-field-desc"
            />
          </Field>

          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Preis (netto, Cent)" required>
              <Input
                type="number"
                value={form.price_cents}
                onChange={(e) => set("price_cents", e.target.value)}
                placeholder="z. B. 2900 für 29 €"
                data-testid="admin-shop-field-price"
              />
              <div className="text-[11px] text-zinc-400 mt-1">= {fmtEuro(Number(form.price_cents) || 0)}</div>
            </Field>
            <Field label="Einheit">
              <Input
                value={form.unit_label}
                onChange={(e) => set("unit_label", e.target.value)}
                placeholder="Stück · 10er Pack"
                data-testid="admin-shop-field-unit"
              />
            </Field>
            <Field label="Sortierung">
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => set("sort_order", e.target.value)}
                placeholder="0–999"
                data-testid="admin-shop-field-order"
              />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Größen (Komma-getrennt)">
              <Input
                value={(form.sizes || []).join(", ")}
                onChange={(e) => set("sizes", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                placeholder="S, M, L, XL, XXL"
                data-testid="admin-shop-field-sizes"
              />
            </Field>
            <Field label="Farben (Komma-getrennt)">
              <Input
                value={(form.colors || []).join(", ")}
                onChange={(e) => set("colors", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                placeholder="Schwarz, Weiß, Navy"
                data-testid="admin-shop-field-colors"
              />
            </Field>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Fallback-Icon">
              <Input
                value={form.icon}
                onChange={(e) => set("icon", e.target.value)}
                placeholder="Lucide-Icon-Name (z. B. Shirt)"
                data-testid="admin-shop-field-icon"
              />
            </Field>
            <Field label="Akzentfarbe (Gradient)">
              <Select value={form.color} onValueChange={(v) => set("color", v)}>
                <SelectTrigger data-testid="admin-shop-field-color"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COLOR_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Verfügbarkeit">
              <Select value={form.in_stock ? "yes" : "no"} onValueChange={(v) => set("in_stock", v === "yes")}>
                <SelectTrigger data-testid="admin-shop-field-stock"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Verfügbar (sichtbar im Shop)</SelectItem>
                  <SelectItem value="no">Ausverkauft (im Shop versteckt)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </div>

        <SheetFooter className="flex-row gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="admin-shop-cancel">
            Abbrechen
          </Button>
          <Button onClick={save} disabled={saving} className="brand-gradient-bg text-white" data-testid="admin-shop-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            {isCreate ? "Produkt anlegen" : "Änderungen speichern"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.18em] font-bold text-zinc-500">
        {label}{required && <span className="text-rose-500 ml-1">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
